import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/api';
import { User } from '../types';

const ManageUsersPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState('');

  const canViewUsers = !!user?.permissions?.includes('view_users');
  const canChangeRoles = !!user?.permissions?.includes('change_user_roles');
  const canDeleteUsers = !!user?.permissions?.includes('delete_users');

  const loadUsers = () => {
    setLoading(true);
    userService
      .list()
      .then((response) => setUsers(response.data))
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (canViewUsers) {
      loadUsers();
    } else {
      setLoading(false);
    }
  }, [canViewUsers]);

  const handleRoleChange = async (id: string, role: User['role']) => {
    setSavingId(id);
    setError(null);
    try {
      const response = await userService.updateRole(id, role);
      setUsers((current) => current.map((item) => item.id === id ? response.data.user : item));
    } catch (requestError: any) {
      setError(requestError.response?.data?.error || 'Failed to update role');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!deleteReason.trim() || deleteTargetId !== id) {
      setError('Enter a deletion reason before removing this user');
      return;
    }

    setSavingId(id);
    setError(null);
    try {
      await userService.delete(id, deleteReason.trim());
      setUsers((current) => current.filter((item) => item.id !== id));
      setDeleteTargetId(null);
      setDeleteReason('');
    } catch (requestError: any) {
      setError(requestError.response?.data?.error || 'Failed to delete user');
    } finally {
      setSavingId(null);
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!canViewUsers) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className="page-shell">
      <div className="container">
        <div className="page-intro">
          <div>
            <p className="eyebrow">Owner</p>
            <h1 className="page-heading">Manage user roles</h1>
            <p className="page-copy">Only the owner can promote, demote, or delete users. Invite onboarding links are managed from the separate invite page.</p>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {loading ? (
          <div className="card">Loading users...</div>
        ) : (
          <div className="card users-table-card">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Change role</th>
                  <th>Delete user</th>
                </tr>
              </thead>
              <tbody>
                {users.map((listedUser) => (
                  <tr key={listedUser.id}>
                    <td>{listedUser.full_name}</td>
                    <td>{listedUser.username}</td>
                    <td><span className={`role-pill role-${listedUser.role}`}>{listedUser.role}</span></td>
                    <td>
                      <select
                        className="form-input compact-input"
                        value={listedUser.role}
                        disabled={!canChangeRoles || savingId === listedUser.id || listedUser.id === user?.id}
                        onChange={(event) => handleRoleChange(listedUser.id, event.target.value as User['role'])}
                      >
                        <option value="member">member</option>
                        <option value="robot">robot</option>
                        <option value="staff">staff</option>
                        <option value="admin">admin</option>
                        <option value="owner">owner</option>
                      </select>
                    </td>
                    <td>
                      <div className="delete-user-stack">
                        <input
                          className="form-input compact-input"
                          placeholder="Reason required"
                          value={deleteTargetId === listedUser.id ? deleteReason : ''}
                          disabled={!canDeleteUsers || savingId === listedUser.id || listedUser.id === user?.id || listedUser.role === 'owner'}
                          onFocus={() => {
                            setDeleteTargetId(listedUser.id);
                            setDeleteReason('');
                          }}
                          onChange={(event) => {
                            setDeleteTargetId(listedUser.id);
                            setDeleteReason(event.target.value);
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger"
                          disabled={!canDeleteUsers || savingId === listedUser.id || listedUser.id === user?.id || listedUser.role === 'owner'}
                          onClick={() => handleDelete(listedUser.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageUsersPage;
